/**
 * usePlaces — data + sync-state hook for Plaatsen.
 *
 * Sync-state truthfulness (spec §8.5 P0):
 * - every place/draft carries exactly one canonical SyncSaveState;
 * - a failed cloud write keeps the form data, stores a device-only draft in
 *   localStorage ('rust-places-drafts') and never claims a cloud save;
 * - queued/failed drafts retry automatically when the connection returns.
 *
 * All persistence goes through repositories.places. Photo uploads use
 * repositories.places.uploadImage; the repository owns user-scoped paths and
 * orphan cleanup on remove/replace. Display URLs are signed-only.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toAppError } from '@/lib/supabase/errors'
import { repositories } from '@/services/repositories'
import { useSync } from '@/services/sync'
import type { AppError, NewPlace, Place, SyncSaveState } from '@/types'
import { dataUrlToFile } from '@/features/media'
import {
  draftKeyFor,
  loadPlaceDrafts,
  removePlaceDraft,
  savePlaceDraft,
  writePlaceDrafts,
  type PlaceDraft,
  type PlaceDraftMap,
} from './drafts'

export type PlaceFormValues = {
  objectLabel: string
  category: string
  subzone: string
  exactLocation: string
  notes: string
}

export type SavePlaceInput = {
  placeId: string | null
  values: PlaceFormValues
  imageFile: File | null
  imageDraftDataUrl: string | null
  /** True when the user removed the existing photo without replacing it. */
  removeImage: boolean
}

export type SavePlaceResult =
  | { ok: true }
  | { ok: false; error: AppError; state: SyncSaveState }

export type PlacesLoadStatus = 'loading' | 'ready' | 'error'

function toNewPlace(values: PlaceFormValues): NewPlace {
  const subzone = values.subzone.trim()
  const exactLocation = values.exactLocation.trim()
  const notes = values.notes.trim()
  return {
    category: values.category,
    objectLabel: values.objectLabel.trim(),
    ...(subzone ? { subzone } : {}),
    ...(exactLocation ? { exactLocation } : {}),
    ...(notes ? { notes } : {}),
  }
}

function draftStateFor(error: AppError, online: boolean): SyncSaveState {
  if (!online) return 'sync_queued'
  if (error.code === 'SUPABASE_NOT_CONFIGURED') return 'local_saved'
  return 'sync_failed'
}

export function usePlaces() {
  const { online, pendingWrites } = useSync()
  const [places, setPlaces] = useState<Place[]>([])
  const [status, setStatus] = useState<PlacesLoadStatus>('loading')
  const [error, setError] = useState<AppError | null>(null)
  const [syncStates, setSyncStates] = useState<Record<string, SyncSaveState>>({})
  const [drafts, setDrafts] = useState<PlaceDraftMap>(() => loadPlaceDrafts())
  const retryingRef = useRef(false)

  const setPlaceState = useCallback((placeId: string, state: SyncSaveState) => {
    setSyncStates((previous) => ({ ...previous, [placeId]: state }))
  }, [])

  const refresh = useCallback(async () => {
    setStatus('loading')
    setError(null)
    try {
      const list = await repositories.places.list()
      setPlaces(list)
      setSyncStates((previous) => {
        const next: Record<string, SyncSaveState> = {}
        for (const place of list) {
          // Keep an explicit failure visible instead of silently upgrading it.
          next[place.id] = previous[place.id] === 'sync_failed' ? 'sync_failed' : 'cloud_saved'
        }
        return next
      })
      setStatus('ready')
    } catch (loadError) {
      setError(toAppError(loadError, 'database'))
      setStatus('error')
    }
  }, [])

  const refreshSilently = useCallback(async () => {
    try {
      const list = await repositories.places.list()
      setPlaces(list)
    } catch {
      // Keep the current list; the next explicit refresh surfaces the error.
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const savePlace = useCallback(
    async (input: SavePlaceInput): Promise<SavePlaceResult> => {
      const data = toNewPlace(input.values)
      if (input.placeId) setPlaceState(input.placeId, 'syncing')
      try {
        let saved = input.placeId
          ? await repositories.places.update(input.placeId, data)
          : await repositories.places.create(data)
        setPlaceState(saved.id, 'syncing')
        if (input.imageFile) {
          saved = await repositories.places.uploadImage(saved.id, input.imageFile)
        } else if (input.removeImage && saved.imagePath) {
          saved = await repositories.places.removeImage(saved.id)
        }
        setPlaceState(saved.id, 'cloud_saved')
        setDrafts(removePlaceDraft(draftKeyFor(input.placeId)))
        await refreshSilently()
        return { ok: true }
      } catch (saveError) {
        const appError = toAppError(saveError, 'database')
        const state = draftStateFor(appError, online)
        if (input.placeId) setPlaceState(input.placeId, state)
        const draft: PlaceDraft = {
          placeId: input.placeId,
          objectLabel: data.objectLabel,
          category: data.category,
          subzone: data.subzone ?? '',
          exactLocation: data.exactLocation ?? '',
          notes: data.notes ?? '',
          imageDataUrl: input.imageDraftDataUrl,
          state,
          updatedAt: new Date().toISOString(),
        }
        setDrafts(savePlaceDraft(draftKeyFor(input.placeId), draft))
        return { ok: false, error: appError, state }
      }
    },
    [online, refreshSilently, setPlaceState],
  )

  const deletePlace = useCallback(
    async (placeId: string): Promise<boolean> => {
      setPlaceState(placeId, 'syncing')
      try {
        await repositories.places.remove(placeId)
        setPlaces((previous) => previous.filter((place) => place.id !== placeId))
        setSyncStates((previous) => {
          const next = { ...previous }
          delete next[placeId]
          return next
        })
        setDrafts(removePlaceDraft(placeId))
        return true
      } catch {
        setPlaceState(placeId, 'sync_failed')
        return false
      }
    },
    [setPlaceState],
  )

  const persistDraft = useCallback((draft: PlaceDraft) => {
    setDrafts(savePlaceDraft(draftKeyFor(draft.placeId), draft))
  }, [])

  const discardDraft = useCallback((key: string) => {
    setDrafts(removePlaceDraft(key))
  }, [])

  const setDraftState = useCallback((key: string, state: SyncSaveState) => {
    const current = loadPlaceDrafts()
    const draft = current[key]
    if (!draft) return
    current[key] = { ...draft, state, updatedAt: new Date().toISOString() }
    writePlaceDrafts(current)
    setDrafts(current)
  }, [])

  const retryDraft = useCallback(
    async (key: string): Promise<boolean> => {
      const draft = loadPlaceDrafts()[key]
      if (!draft) return false
      setDraftState(key, 'syncing')
      try {
        const data = toNewPlace({
          objectLabel: draft.objectLabel,
          category: draft.category,
          subzone: draft.subzone,
          exactLocation: draft.exactLocation,
          notes: draft.notes,
        })
        let saved = draft.placeId
          ? await repositories.places.update(draft.placeId, data)
          : await repositories.places.create(data)
        if (draft.imageDataUrl) {
          const file = await dataUrlToFile(draft.imageDataUrl, 'foto.jpg')
          saved = await repositories.places.uploadImage(saved.id, file)
        }
        setPlaceState(saved.id, 'cloud_saved')
        setDrafts(removePlaceDraft(key))
        await refreshSilently()
        return true
      } catch (retryError) {
        const appError = toAppError(retryError, 'database')
        setDraftState(key, draftStateFor(appError, online))
        return false
      }
    },
    [online, refreshSilently, setDraftState, setPlaceState],
  )

  const retryAllDrafts = useCallback(async () => {
    if (retryingRef.current) return
    retryingRef.current = true
    try {
      for (const key of Object.keys(loadPlaceDrafts())) {
        // Sequential on purpose: one in-flight write per draft, fresh read each time.
        await retryDraft(key)
      }
    } finally {
      retryingRef.current = false
    }
  }, [retryDraft])

  // Upload retries on reconnect (spec §8.5) + one attempt on mount when online.
  const wasOnlineRef = useRef(online)
  useEffect(() => {
    const wasOnline = wasOnlineRef.current
    wasOnlineRef.current = online
    if (online && !wasOnline && Object.keys(loadPlaceDrafts()).length > 0) {
      void retryAllDrafts()
    }
  }, [online, retryAllDrafts])

  const mountedRetryRef = useRef(false)
  useEffect(() => {
    if (mountedRetryRef.current) return
    mountedRetryRef.current = true
    if (online && Object.keys(loadPlaceDrafts()).length > 0) {
      void retryAllDrafts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    places,
    status,
    error,
    syncStates,
    drafts,
    online,
    pendingWrites,
    refresh,
    savePlace,
    deletePlace,
    persistDraft,
    discardDraft,
    retryDraft,
  }
}
